/**
 * =============================================================================
 * App Teams Management Page
 * =============================================================================
 *
 * Manage app development teams with Keycloak integration and Harbor projects.
 *
 * Features:
 * - List all app teams
 * - Create new teams (with automatic Harbor project creation)
 * - View team details and members
 * - Activate/Deactivate teams
 *
 * Access:
 * - system_admin, app_admin: Full access
 * - app_dev: View only (their teams)
 */

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { appTeamsApi, AppTeam, AppTeamCreate, AppTeamCreateResponse, HarborCredentials } from '../services/api'
import DataTable, { Column } from '../components/DataTable'
import Modal from '../components/Modal'
import { useAuth } from '../auth/AuthProvider'

// =============================================================================
// Create Team Modal
// =============================================================================

interface CreateTeamModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: (response: AppTeamCreateResponse) => void
}

const CreateTeamModal: React.FC<CreateTeamModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const queryClient = useQueryClient()
  const [formData, setFormData] = useState<AppTeamCreate>({
    name: '',
    team_email: '',
    app_limit: 5,
  })
  const [error, setError] = useState<string | null>(null)

  const createMutation = useMutation({
    mutationFn: (data: AppTeamCreate) => appTeamsApi.create(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['app-teams'] })
      onSuccess(response.data)
      setFormData({ name: '', team_email: '', app_limit: 5 })
      setError(null)
    },
    onError: (err: unknown) => {
      const error = err as { response?: { data?: { detail?: string } } }
      const detail = error.response?.data?.detail
      setError(typeof detail === 'string' ? detail : 'Failed to create team')
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.name.trim()) {
      setError('Team name is required')
      return
    }
    const payload: AppTeamCreate = {
      name: formData.name.trim(),
      app_limit: formData.app_limit,
    }
    if (formData.team_email && formData.team_email.trim()) {
      payload.team_email = formData.team_email.trim()
    }
    createMutation.mutate(payload)
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create App Team">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Team Name *
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., Alpha Team"
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            Will create Harbor project: rehapiano-app-team-{formData.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Team Email
          </label>
          <input
            type="email"
            value={formData.team_email || ''}
            onChange={(e) => setFormData({ ...formData, team_email: e.target.value })}
            placeholder="team@example.com"
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            App Limit
          </label>
          <input
            type="number"
            min={1}
            max={100}
            value={formData.app_limit}
            onChange={(e) => setFormData({ ...formData, app_limit: parseInt(e.target.value) || 5 })}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <p className="text-xs text-gray-500 mt-1">Maximum number of apps this team can create</p>
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={createMutation.isPending}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {createMutation.isPending ? 'Creating...' : 'Create Team'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

// =============================================================================
// Credentials Modal
// =============================================================================

interface CredentialsModalProps {
  isOpen: boolean
  onClose: () => void
  credentials: HarborCredentials | null
  teamName: string
}

const CredentialsModal: React.FC<CredentialsModalProps> = ({ isOpen, onClose, credentials, teamName }) => {
  const [copied, setCopied] = useState<string | null>(null)

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text)
    setCopied(field)
    setTimeout(() => setCopied(null), 2000)
  }

  if (!credentials) return null

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Harbor Credentials - ${teamName}`}>
      <div className="space-y-4">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-yellow-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div>
              <p className="text-yellow-800 font-medium">Save these credentials!</p>
              <p className="text-yellow-700 text-sm mt-1">
                The robot secret is only shown once during creation. Store it securely.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">Registry URL</label>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-gray-100 px-3 py-2 rounded text-sm font-mono">
                {credentials.registry_url}
              </code>
              <button
                onClick={() => copyToClipboard(credentials.registry_url, 'registry')}
                className="px-2 py-2 text-gray-500 hover:text-gray-700"
              >
                {copied === 'registry' ? '✓' : '📋'}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">Project Name</label>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-gray-100 px-3 py-2 rounded text-sm font-mono">
                {credentials.project_name}
              </code>
              <button
                onClick={() => copyToClipboard(credentials.project_name, 'project')}
                className="px-2 py-2 text-gray-500 hover:text-gray-700"
              >
                {copied === 'project' ? '✓' : '📋'}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">Robot Account</label>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-gray-100 px-3 py-2 rounded text-sm font-mono break-all">
                {credentials.robot_account}
              </code>
              <button
                onClick={() => copyToClipboard(credentials.robot_account, 'robot')}
                className="px-2 py-2 text-gray-500 hover:text-gray-700"
              >
                {copied === 'robot' ? '✓' : '📋'}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">Robot Secret</label>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-gray-100 px-3 py-2 rounded text-sm font-mono break-all">
                {credentials.robot_secret}
              </code>
              <button
                onClick={() => copyToClipboard(credentials.robot_secret, 'secret')}
                className="px-2 py-2 text-gray-500 hover:text-gray-700"
              >
                {copied === 'secret' ? '✓' : '📋'}
              </button>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-4 mt-4">
          <p className="text-sm font-medium text-gray-700 mb-2">Docker Login Command:</p>
          <code className="block bg-gray-800 text-green-400 px-3 py-2 rounded text-sm font-mono overflow-x-auto">
            docker login {credentials.registry_url} -u {credentials.robot_account}
          </code>
        </div>

        <div className="flex justify-end pt-4">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  )
}

// =============================================================================
// Main Page Component
// =============================================================================

const AppTeamsPage: React.FC = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showCredentialsModal, setShowCredentialsModal] = useState(false)
  const [newCredentials, setNewCredentials] = useState<HarborCredentials | null>(null)
  const [newTeamName, setNewTeamName] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('')

  const isAdmin = user?.roles?.includes('system_admin') || user?.roles?.includes('app_admin')

  // Fetch app teams
  const { data, isLoading } = useQuery({
    queryKey: ['app-teams', statusFilter],
    queryFn: () => appTeamsApi.list(statusFilter ? { status: statusFilter } : undefined),
  })

  // Status toggle mutation
  const toggleStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      if (status === 'active') {
        return appTeamsApi.deactivate(id)
      } else {
        return appTeamsApi.activate(id)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['app-teams'] })
    },
  })

  const handleCreateSuccess = (response: AppTeamCreateResponse) => {
    setShowCreateModal(false)
    setNewTeamName(response.name)
    setNewCredentials(response.harbor_credentials)
    setShowCredentialsModal(true)
  }

  // Table columns
  const columns: Column<AppTeam>[] = [
    {
      key: 'name',
      header: 'Team',
      render: (team) => (
        <div>
          <div className="font-medium text-blue-600 hover:text-blue-800 cursor-pointer" onClick={() => navigate(`/app-teams/${team.id}`)}>
            {team.name}
          </div>
          <div className="text-xs text-gray-500">{team.keycloak_group_path}</div>
        </div>
      ),
    },
    {
      key: 'harbor',
      header: 'Harbor Project',
      render: (team) => (
        <code className="text-sm bg-gray-100 px-2 py-1 rounded">
          {team.harbor_project_name}
        </code>
      ),
    },
    {
      key: 'email',
      header: 'Contact',
      render: (team) => team.team_email || '-',
    },
    {
      key: 'stats',
      header: 'Stats',
      render: (team) => (
        <div className="text-sm">
          <div>{team.member_count} members</div>
          <div className="text-gray-500">{team.app_count} / {team.app_limit} apps</div>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (team) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          team.status === 'active'
            ? 'bg-green-100 text-green-800'
            : 'bg-gray-100 text-gray-800'
        }`}>
          {team.status}
        </span>
      ),
    },
    ...(isAdmin ? [{
      key: 'actions',
      header: 'Actions',
      render: (team: AppTeam) => (
        <div className="flex gap-2">
          <button
            onClick={() => navigate(`/app-teams/${team.id}`)}
            className="text-blue-600 hover:text-blue-800 text-sm"
          >
            View
          </button>
          <button
            onClick={() => toggleStatusMutation.mutate({ id: team.id, status: team.status })}
            className={`text-sm ${
              team.status === 'active'
                ? 'text-orange-600 hover:text-orange-800'
                : 'text-green-600 hover:text-green-800'
            }`}
            disabled={toggleStatusMutation.isPending}
          >
            {team.status === 'active' ? 'Deactivate' : 'Activate'}
          </button>
        </div>
      ),
    }] : []) as Column<AppTeam>[],
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">App Teams</h2>
          <p className="text-sm text-gray-600">
            Manage app development teams with Harbor registry integration.
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create Team
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-2"
        >
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        data={data?.data || []}
        loading={isLoading}
        getRowKey={(team) => team.id}
        emptyMessage="No app teams found"
      />

      {/* Modals */}
      <CreateTeamModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleCreateSuccess}
      />

      <CredentialsModal
        isOpen={showCredentialsModal}
        onClose={() => setShowCredentialsModal(false)}
        credentials={newCredentials}
        teamName={newTeamName}
      />
    </div>
  )
}

export default AppTeamsPage
